export const sortItemsForCulturalInstitutions = (url: string, contentData: any) => {
  // Input validation
  if (!url || typeof url !== 'string') {
    console.warn('Invalid URL provided to sortItemsForCulturalInstitutions')
    return contentData
  }

  if (!contentData?.values?.page?.modules) {
    console.warn('Invalid content data structure provided to sortItemsForCulturalInstitutions')
    return contentData
  }

  // Create a deep copy to avoid mutating original data
  const dataToSend = JSON.parse(JSON.stringify(contentData))
  const modulesToSend = dataToSend.values.page.modules

  if (!Array.isArray(modulesToSend)) {
    console.warn('Modules is not an array in sortItemsForCulturalInstitutions')
    return contentData
  }

  // if url includes '/cultural-institutions'
  if (url.includes('/cultural-institutions')) {
    // For all the Information Modules after each Headline title,
    // we need to sort all cards alphabetically
    // across all Information modules that come after each Headline
    let currentSectionCards: any[] = []
    let currentSectionModules: any[] = []

    const processSectionCards = () => {
      if (currentSectionCards.length > 0) {
        try {
          // Sort all cards in this section alphabetically by title
          // Filter out cards with invalid titles first
          const validCards = currentSectionCards.filter(card =>
            card?.title && typeof card.title === 'string'
          )

          validCards.sort((a: any, b: any) => {
            try {
              return a.title.localeCompare(b.title, undefined, {
                numeric: true,
                sensitivity: 'base'
              })
            } catch (error) {
              console.warn('Error comparing titles:', a.title, b.title, error)
              return 0
            }
          })

          // Clear existing fields from all modules in this section
          currentSectionModules.forEach((module: any) => {
            if (!module?.fields) {
              console.warn('Module missing fields property:', module)
              return
            }

            const columnNumberStr = module.fields.info_grp1_column_num
            const columnNumber = parseInt(columnNumberStr, 10) || 0

            if (columnNumber > 10) { // Safety limit
              console.warn('Unusually high column number:', columnNumber)
              return
            }

            Array.from({ length: columnNumber }, (_, i) => i + 1).forEach((i) => {
              try {
                // Clear all possible fields for each group
                delete module.fields[`info_grp${i}_title`]
                delete module.fields[`info_grp${i}_subtitle`]
                delete module.fields[`info_grp${i}_img`]
                delete module.fields[`info_grp${i}_body`]
                delete module.fields[`info_grp${i}_cta_label`]
                delete module.fields[`info_grp${i}_cta_href`]
              } catch (error) {
                console.warn('Error clearing module fields:', error)
              }
            })

            // Preserve global configuration fields that shouldn't be sorted
            // info_grp1_column_num, info_grp5_show_global_cta, info_grp5_global_cta_url, etc.
            // These are preserved automatically since we only delete info_grp1-4 fields
          })

          // Redistribute sorted cards across modules
          let cardIndex = 0
          currentSectionModules.forEach((module: any) => {
            if (!module?.fields) return

            const columnNumberStr = module.fields.info_grp1_column_num
            const columnNumber = parseInt(columnNumberStr, 10) || 0

            if (columnNumber > 10) return // Safety limit

            // Fill this module with cards
            Array.from({ length: columnNumber }, (_, i) => i + 1).forEach((i) => {
              if (cardIndex < validCards.length) {
                const card = validCards[cardIndex]
                if (card && card.title) {
                  try {
                    // Always set title (required field)
                    module.fields[`info_grp${i}_title`] = card.title

                    // Set optional fields only if they exist and are not empty
                    if (card.subtitle && card.subtitle !== null) {
                      module.fields[`info_grp${i}_subtitle`] = card.subtitle
                    }
                    if (card.img && card.img !== null) {
                      module.fields[`info_grp${i}_img`] = card.img
                    }
                    if (card.body && card.body !== null) {
                      module.fields[`info_grp${i}_body`] = card.body
                    }
                    if (card.cta_label && card.cta_label !== null) {
                      module.fields[`info_grp${i}_cta_label`] = card.cta_label
                    }
                    if (card.cta_href && card.cta_href !== null) {
                      module.fields[`info_grp${i}_cta_href`] = card.cta_href
                    }

                    cardIndex++
                  } catch (error) {
                    console.warn(`Error setting module fields for info_grp${i}:`, error)
                  }
                }
              }
            })
          })
        } catch (error) {
          console.error('Error processing section cards:', error)
        }
      }
    }

    try {
      modulesToSend.forEach((module: any) => {
        if (!module || typeof module !== 'object') {
          console.warn('Invalid module encountered:', module)
          return
        }

        // When we encounter a Headline title, process the previous section and start a new one
        if (module.moduleName === 'Headline title') {
          processSectionCards()
          currentSectionCards = []
          currentSectionModules = []
        } else if (module.moduleName === 'Information') {
          if (!module.fields) {
            console.warn('Information module missing fields:', module)
            return
          }

          const columnNumberStr = module.fields.info_grp1_column_num
          const columnNumber = parseInt(columnNumberStr, 10) || 0

          if (columnNumber > 10) { // Safety limit
            console.warn('Unusually high column number:', columnNumber, 'in module:', module.id)
            return
          }

          currentSectionModules.push(module)

          // Collect all cards from this module
          Array.from({ length: columnNumber }, (_, i) => i + 1).forEach((i) => {
            try {
              const title = module.fields[`info_grp${i}_title`]
              if (title && typeof title === 'string' && title.trim() !== '') {
                // Safely extract all fields with fallbacks and validation
                const subtitle = module.fields[`info_grp${i}_subtitle`]
                const img = module.fields[`info_grp${i}_img`]
                const body = module.fields[`info_grp${i}_body`]
                const cta_label = module.fields[`info_grp${i}_cta_label`]
                const cta_href = module.fields[`info_grp${i}_cta_href`]

                // Helper function to safely process string fields
                const processStringField = (field: any): string | null => {
                  if (!field || typeof field !== 'string') return null
                  const trimmed = field.trim()
                  return trimmed === '' ? null : trimmed
                }

                currentSectionCards.push({
                  title: title.trim(),
                  subtitle: processStringField(subtitle),
                  img: processStringField(img),
                  body: processStringField(body),
                  cta_label: processStringField(cta_label),
                  cta_href: processStringField(cta_href)
                })
              }
            } catch (error) {
              console.warn(`Error collecting card data for info_grp${i}:`, error)
            }
          })
        }
      })

      // Process the last section
      processSectionCards()
    } catch (error) {
      console.error('Error processing modules:', error)
      return contentData // Return original data if processing fails
    }
  }

  return dataToSend
}